/**
 * sentry-noise-filter.js — own-frame gate for Sentry `beforeSend`.
 *
 * Root-cause replacement for the hand-grown per-message `ignoreErrors`
 * denylist. A Sentry issue is actionable ONLY if its stack reaches code
 * that Timbrica ships. Browser extensions, injected privacy scripts
 * (DuckDuckGo `content-scope-scripts`), ad SDKs, corporate AV — every
 * one of them shares a single signature: not a single stack frame from
 * a `timbrica.com` origin. Such an event is, by construction, not our
 * bug and not investigable — there is no file/line of ours to open.
 *
 * `isForeignNoise(event)` returns true for exactly those, so `beforeSend`
 * can drop them with ONE principled rule instead of someone appending
 * `ignoreErrors` entry #25 the next time a new extension misbehaves.
 *
 * Scope / safety — deliberately conservative, errs toward KEEPING:
 *  - Only judges events that carry an `exception`. An event with no
 *    exception (our explicit `Sentry.captureMessage`) → not noise; we
 *    asked for it on purpose.
 *  - An exception whose stack contains ≥1 frame on a timbrica.com
 *    origin → not noise. Inline page scripts count: their frame
 *    `filename` IS the page URL (`https://timbrica.com/…`).
 *  - An exception with frames but ZERO own frames → noise.
 *  - An exception with NO stack at all → noise. DuckDuckGo's frameless
 *    `feature named \`pageContext\` was not found` rejection is exactly
 *    this shape (Sentry TIMBRICA-FRONTEND-5N).
 *  - The lone false-negative class — a frameless promise rejection from
 *    our OWN async code — is already mirrored to `__aeToolError`
 *    telemetry and is anyway uninvestigable in Sentry, so dropping it
 *    loses nothing actionable.
 *  - Any internal error in the filter itself → returns false (keep the
 *    event); the gate must never swallow a real bug.
 */
(function () {
    'use strict';

    // A frame belongs to us if its file lives on a timbrica.com origin.
    // The (^ | // | .) prefix guard allows timbrica.com, assets./cdn.
    // timbrica.com and inline-script frames (filename === the page URL),
    // while rejecting look-alikes such as `evil-timbrica.com`.
    var OWN = /(^|\/\/|\.)timbrica\.com[\/:]/i;

    /**
     * Пол для инлайн-кадра документа.
     *
     * Кадр инлайн-скрипта носит имя САМОЙ СТРАНИЦЫ, поэтому чужой скрипт,
     * внедрённый в документ (обёртка браузера, расширение), проходит проверку
     * происхождения как «наш» и приносит СВОИ координаты. Так в Sentry жили
     * TIMBRICA-FRONTEND-GS и -92: `global code` с позицией, которой в нашем
     * документе не существует (строка 1, колонка 31 — при том что строка 1
     * это `<!doctype html>`, 15 символов).
     *
     * Различающее свойство: наш инлайн-скрипт не может начаться раньше
     * ЧЕТВЁРТОЙ строки — строки 1–3 это `<!doctype html>`, `<html …>` и
     * `<head> <meta charset>`. Замерено на обоих доменах и в RTL; MinifyHtml
     * намеренно экранирует `<script>` и не трогает инлайн-JS, поэтому пол
     * стабилен. Инвариант держится тестом InlineScriptFloorTest — если
     * вёрстка головы изменится, упадёт ТЕСТ, а не наступит тишина в Sentry.
     *
     * ⚠️ 12.09.2026 тест это и поймал, и стоит записать, чем кончилось. Новый
     * заслон минификатора знал про свои метки, поэтому его проход зазоров
     * схлопывал и промежуток перед первым `<script>` (метка съедает `<`, и
     * прежний проход такой зазор не видел) — первая строка нашего инлайн-кода
     * уехала на ТРЕТЬЮ. Пол сперва опустили до 3, и это было неверно: кадр
     * TIMBRICA-FRONTEND-GS приходит ровно «строка 3, колонка 185», то есть
     * ровно туда, где теперь стоит наш собственный код, — различать стало
     * нечем. Правильный ход оказался обратным: token-aware зазор в
     * минификаторе снят. Он стоил 14 140 байт на корпусе, но НА ПРОВОДЕ это
     * 97 сжатых байт со страницы (0.054 %) — шум, за который не покупают
     * рабочий прибор.
     */
    var FIRST_INLINE_SCRIPT_LINE = 4;

    /**
     * Кадр, приписанный ДОКУМЕНТУ, с колонкой, которой в нашей разметке не бывает.
     * Самая длинная строка наших страниц — 48 868 знаков (блок <style> в голове,
     * замер 20.10.2026 по /ru, /ru/spin-wheel, /ru/audio-to-notes, /ru/video-editor);
     * строка инлайн-скрипта короче. Встроенный браузер приложения (WKWebView) кладёт
     * в страницу свой минифицированный скрипт одной строкой, и WebKit приписывает
     * его кадры адресу страницы: Sentry TIMBRICA-FRONTEND-QS — «'#029fx0' is not a
     * valid selector» @ /ru/spin-wheel:11:383258, а строка 11 у нас — 314 знаков.
     * Порог взят с запасом в четыре раза к нашей самой длинной строке; держит его
     * InlineScriptFloorTest (наша строка обязана быть короче половины порога).
     */
    var DOC_COLUMN_CEIL = 200000;

    /**
     * Кадр, исполнявшийся ВНУТРИ расширения браузера. Safari прячет адрес
     * скрипта расширения за `webkit-masked-url://hidden/`; остальные движки
     * называют свою схему. Якорь `^` — чтобы наш файл с таким словом в пути
     * (`/js/chrome-extension-helper.js`) сюда не попал.
     *
     * Кадр расширения В ЛЮБОМ месте стека ОДНОГО исключения = оно прошло через
     * чужой код. До 15.09.2026 гейт спрашивал только «есть ли хоть один наш
     * кадр», а denyUrls Sentry смотрит только на самый молодой. Между ними жил класс:
     * расширение перехватывает DOM-API (append, querySelectorAll…), наш
     * инлайн-скрипт его зовёт, хук расширения бросает — и стек несёт и их
     * кадры, и наши. Так пришёл TIMBRICA-FRONTEND-K5: «Can't find variable:
     * unsafeWindow» (менеджер юзерскриптов Safari, чей код попал в мир
     * страницы), кадры `webkit-masked-url://hidden/` над нашим `global code`.
     * В таком стеке нам нечего открыть: ни один наш файл этой ошибки не бросал.
     * Цена правила: дефект нашего кода, ВЫЗВАННЫЙ расширением (кликер,
     * автозаполнение), тоже уйдёт — но такой дефект приходит и без
     * расширения, из обычного клика, со стеком без чужих кадров.
     * Правило — ПО ЗНАЧЕНИЮ, не по событию: в цепочке исключений (cause) чужое
     * значение отбрасывается, а наше рядом с ним по-прежнему держит событие.
     */
    var EXTENSION = /^(?:webkit-masked-url|chrome-extension|moz-extension|safari-web-extension|safari-extension|ms-browser-extension):/i;

    function frameIsExtension(f) {
        var loc = f && (f.abs_path || f.absPath || f.filename || '');
        return typeof loc === 'string' && EXTENSION.test(loc);
    }

    function crossedExtension(frames) {
        for (var j = 0; j < frames.length; j++) if (frameIsExtension(frames[j])) return true;
        return false;
    }

    /**
     * Кадры СТРАНИЦЫ с именами, которых нет ни в одном нашем инлайн-скрипте.
     * Пол по строке (FIRST_INLINE_SCRIPT_LINE) ловит только внедрение в первые
     * строки; код, который хост кладёт в страницу целиком (Chrome for iOS,
     * приложение Google), приходит с любыми координатами — Sentry MN: Ok/Qk в
     * «/ru/…:226:63», и frameIsOurs считал его нашим. Решает не этот файл:
     * владелец вопроса — window.__docFramesForeign (timbrica-core.js), его же
     * спрашивают window.onerror и ранний перехватчик в layout, — второй копии
     * правила быть не должно. Нет владельца (ядро не загрузилось) или он
     * бросил — отвечаем «не доказано», и событие остаётся.
     * Как и crossedExtension — по ЗНАЧЕНИЮ: наше значение в той же цепочке
     * событие держит.
     */
    function docFramesForeign(frames) {
        try { return typeof window.__docFramesForeign === 'function' && window.__docFramesForeign(frames) === true; }
        catch (_) { return false; }
    }

    /**
     * Значение, которое Firefox ПОДМЕНИЛ: бросила функция расширения, отданная
     * странице через exportFunction, и странице досталась заглушка без стека
     * и без файла. SDK дорисовывает ей кадр «?» с адресом документа и строкой 0,
     * а frameIsOurs принимает такой кадр за наш инлайн (TIMBRICA-FRONTEND-P9).
     * Это Firefox-аналог crossedExtension: кадры расширения движок спрятал, и
     * его код выдаёт только подмена. Признак ставит window.__privilegedThrow
     * (timbrica-core.js), второй копии правила здесь нет. Если владельца нет
     * или он бросил, ответ «не доказано», и событие остаётся.
     */
    function privilegedThrow(v) {
        try { return !!v && typeof window.__privilegedThrow === 'function' && window.__privilegedThrow({ name: v.type, message: v.value }) === true; }
        catch (_) { return false; }
    }

    // Наш JS живёт в файлах .js/.mjs; путь без них на нашем origin — это
    // документ, то есть кадр инлайн-скрипта страницы.
    function looksLikeDocument(loc) {
        var path = String(loc).split('?')[0].split('#')[0];
        return !/\.(?:js|mjs|cjs)$/i.test(path);
    }

    /**
     * Кадр из blob:-скрипта нашего origin'а — наш только если это НАШ blob.
     * Свои blob-воркеры мы пишем строками от руки (opfs-writer, проба wasm,
     * pcm-opfs-store, device-score): десятки строк по сотне знаков. Строка в
     * 50 000 знаков внутри blob: — это минифицированная библиотека, а такой
     * код мы через blob: не отдаём; его создаёт расширение или сам браузер
     * (Sentry TIMBRICA-FRONTEND-KP: Яндекс.Браузер, /document-scanner, на
     * 8-й секунде страницы, до выбора файла — три кадра blob:, строка 13,
     * колонка 49 907, ни одного нашего файла). Порог — по КОЛОНКЕ, как у
     * инлайн-кадра порог по строке: наша рукописная строка так далеко не
     * доходит.
     */
    var BLOB_COLUMN_FLOOR = 4096;

    function frameLoc(f) { return f && (f.abs_path || f.absPath || f.filename || f.module || ''); }
    function frameCol(f) { var c = f.colno; if (c == null) c = f.colNo; if (c == null) c = f.col_no; return c; }

    // blob:-скрипты значения, в которых хоть один кадр стоит за порогом колонки:
    // минифицированная библиотека — чужая ЦЕЛИКОМ, включая её короткие строки.
    function minifiedBlobs(frames) {
        var set = {};
        for (var i = 0; i < frames.length; i++) {
            var loc = frameLoc(frames[i]), col = frameCol(frames[i]);
            if (typeof loc === 'string' && /^blob:/i.test(loc) && typeof col === 'number' && col >= BLOB_COLUMN_FLOOR) set[loc] = true;
        }
        return set;
    }

    /**
     * Страница, где лестница исполнила запасную копию ОТЛОЖЕННОГО скрипта встроенным
     * текстом (layout `__jsFallback`, 30.10.2026): у такой копии нет своего src, а
     * WebKit не применяет её `//# sourceURL` к стеку — её кадры стоят на адресе
     * страницы со строки 1 и с колонками минифицированного файла (замер в трёх
     * движках). Значит, на этой странице «кадр документа вне наших строк» больше
     * не доказывает чужой код, и правила по адресу документа молчат. Список
     * пишет сама лестница ДО исполнения копии; премиссу держит
     * NoInlineTextScriptInjectionTest.
     */
    function inlineRecovered() {
        try { var r = window.__fbInlineRecovered; return !!(r && r.length); }
        catch (_) { return false; }
    }

    function frameIsOurs(f, foreignBlobs) {
        if (!f) return false;
        var loc = frameLoc(f);
        if (typeof loc !== 'string' || !OWN.test(loc)) return false;
        if (/^blob:/i.test(loc)) return !(foreignBlobs && foreignBlobs[loc]);
        if (looksLikeDocument(loc) && !inlineRecovered()) {
            var ln = f.lineno;
            if (ln == null) ln = f.lineNo;
            if (ln == null) ln = f.line_no;
            // Позиция есть и она НЕВОЗМОЖНА в нашем документе → кадр не наш.
            // Позиции нет → считаем нашим (гейт ошибается в сторону «оставить»).
            if (typeof ln === 'number' && ln > 0 && ln < FIRST_INLINE_SCRIPT_LINE) return false;
            var col = frameCol(f);
            if (typeof col === 'number' && col > DOC_COLUMN_CEIL) return false;
        }
        return true;
    }

    /**
     * Синтаксическая ошибка, приписанная ДОКУМЕНТУ уже после того, как он
     * загрузился, — не наша. Все наши инлайн-скрипты разбираются во время
     * загрузки; всё, что мы вставляем позже, — <script src> (у него своё
     * имя файла). Премиссу держит NoInlineTextScriptInjectionTest: data-блоки
     * (ld+json) не парсятся как JS, а три legacy-лоадера pdf.js с инлайн-
     * модулем (redact-pdf, sign-pdf, document-reader) перечислены там
     * списком, который может только сокращаться — это единственный наш код,
     * чей SyntaxError после загрузки правило бы проглотило (и чей промис при
     * этом повис бы сам по себе). Значит SyntaxError с именем страницы при
     * readyState === 'complete' — код, внедрённый расширением или обёрткой
     * (Sentry GX: «Missing initializer in const declaration» @ страница:51:13
     * после клика «Старт» на /webrtc-leak-test; GV: «Unexpected identifier
     * 'as'» @ :49:34 в Electron; серия «Unexpected token 'else'» @ :1:219 из
     * Chrome WebView). Пол «строка < 4» их не ловит — позиция внутри
     * внедрённого блока произвольна.
     *
     * Исключение: SyntaxError от JSON.parse — рантайм, а не парсинг, и в его
     * тексте есть слово JSON; у него к тому же есть нативный кадр parse.
     */
    function isLateParseError(vals) {
        if (typeof document === 'undefined' || document.readyState !== 'complete') return false;
        if (inlineRecovered()) return false; // our own inline copy is on this page — its parse error is ours
        for (var i = 0; i < vals.length; i++) {
            var v = vals[i];
            if (!v || v.type !== 'SyntaxError') continue;
            if (/JSON/i.test(String(v.value || ''))) continue;
            var frames = v.stacktrace && v.stacktrace.frames;
            if (!frames || !frames.length) continue;
            var allDocument = true;
            for (var j = 0; j < frames.length; j++) {
                var loc = frames[j] && (frames[j].abs_path || frames[j].absPath || frames[j].filename || '');
                if (typeof loc !== 'string' || !OWN.test(loc) || !looksLikeDocument(loc)) { allDocument = false; break; }
            }
            if (allDocument) return true;
        }
        return false;
    }

    /* Обрыв ДОСТАВКИ нашего файла: «Unexpected end of script/input/EOF» с единственным
     * кадром в нашем же .js. Строки не исполнилось ни одной, лестница уже повторяет
     * файл (`js_truncated` в телеметрии) — в Sentry это не дефект кода, а транспорт
     * (JX: tool-uploader.js 40 342 из 209 822 байт; MA: ffmpeg-processor.js, iOS 15).
     * Любой ДРУГОЙ SyntaxError в нашем файле — настоящая поломка сборки — остаётся. */
    var TRUNCATED_RE = /unexpected (end of (script|input|file)|eof)\b/i;
    function isTruncatedOwnScript(vals) {
        for (var i = 0; i < vals.length; i++) {
            var v = vals[i];
            if (!v || v.type !== 'SyntaxError' || !TRUNCATED_RE.test(String(v.value || ''))) continue;
            var frames = v.stacktrace && v.stacktrace.frames;
            if (!frames || !frames.length) continue;
            var own = true;
            for (var j = 0; j < frames.length; j++) {
                var loc = frames[j] && (frames[j].abs_path || frames[j].absPath || frames[j].filename || '');
                if (typeof loc !== 'string' || !OWN.test(loc) || looksLikeDocument(loc)) { own = false; break; }
            }
            if (own) return true;
        }
        return false;
    }

    /* A FOREIGN BODY under the address of our file: «Unexpected token '<'» (V8) /
     * «expected expression, got '<'» (Firefox) in the first lines of our own .js —
     * HTML came instead of the script (a captive portal, a proxy, an error page on
     * the way). Not a line of ours ran, and the layout's ladder recognises the same
     * message on the same file and fetches it again (`js_wrong_content`, the same
     * pattern and the same «line ≤ 3» bound). Sentry Q5 (16.10 /iq-test, 30.10
     * /audio-metadata): device-capability-gate.js at 1:1, the repeat of that load a
     * moment later answered 200 with our code. A '<' deeper in the file is a real
     * break of the build and stays. */
    var FOREIGN_BODY_RE = /unexpected token '?<'?|expected expression, got '<'/i;
    function isForeignBodyOwnScript(vals) {
        for (var i = 0; i < vals.length; i++) {
            var v = vals[i];
            if (!v || v.type !== 'SyntaxError' || !FOREIGN_BODY_RE.test(String(v.value || ''))) continue;
            var frames = v.stacktrace && v.stacktrace.frames;
            if (!frames || !frames.length) continue;
            var own = true;
            for (var j = 0; j < frames.length; j++) {
                var f = frames[j] || {};
                var loc = f.abs_path || f.absPath || f.filename || '';
                var line = f.lineno != null ? f.lineno : f.lineNo;
                if (typeof loc !== 'string' || !OWN.test(loc) || looksLikeDocument(loc) || !(line <= 3)) { own = false; break; }
            }
            if (own) return true;
        }
        return false;
    }

    /* A parse error from an engine that cannot read the language our files are
     * written in is "this browser is too old", not a defect: no line of ours can
     * be fixed for it. Our own files use ES2020+ syntax (`?.`, `??`, top-level
     * await in modules, class #private), the vendor pass (vendor-es2019) brings
     * libraries to ES2019 — an engine without the ES2020 builtins that shipped
     * with that syntax can parse neither.
     *
     * Judged by what the engine HAS, not by what its User-Agent says: on
     * 16.10.2026 every event of TIMBRICA-FRONTEND-J7 (reorder-pages, «Unexpected
     * reserved word» on top-level await) and PC (emoji-detail, «Unexpected token
     * '.'» on `?.`) came from Hetzner Helsinki addresses whose UA claims Chrome
     * 154 — a crawler on an old engine. Real old browsers did the same: PR (iOS
     * 11.4, `for await`), PN (WebView 66, `?.` in peerjs), PQ (Mypal on Windows
     * XP running a module as a classic script).
     *
     *  - below the floor: no `Promise.allSettled` / `globalThis` (pre-2020:
     *    Chrome < 76, Safari < 13, Firefox < 71), or no module support;
     *  - a lying engine: the UA claims Chromium ≥ 92 (or Firefox ≥ 90 /
     *    Safari ≥ 15.4) and `Array.prototype.at` — shipped in exactly those —
     *    is missing.
     * A SyntaxError on a modern, honest engine stays: that is a real break. */
    var engineVerdict = null;
    function engineBelowFloor(env) {
        if (!env && engineVerdict !== null) return engineVerdict;
        var e = env || (function () {
            var g = (typeof window !== 'undefined') ? window : {};
            var ua = '';
            try { ua = String((g.navigator && g.navigator.userAgent) || ''); } catch (_) {}
            var mod = true;
            try { mod = !!(g.HTMLScriptElement && 'noModule' in g.HTMLScriptElement.prototype); } catch (_) {}
            return {
                ua: ua,
                allSettled: typeof Promise !== 'undefined' && typeof Promise.allSettled === 'function',
                globalThis: typeof globalThis !== 'undefined',
                at: typeof Array.prototype.at === 'function',
                modules: g.HTMLScriptElement ? mod : true,
                propEscapes: (function () { try { new RegExp('\\p{L}', 'u'); return true; } catch (_) { return false; } })()
            };
        })();
        /* Unicode property escapes (`/\p{L}/u`, ES2018) — 40 of our files use them. The
         * UXP engines (Basilisk, Pale Moon, Mypal on Windows XP) and SeaMonkey 2.53
         * backported allSettled and globalThis but not this syntax, so they passed
         * the builtin check and still die parsing our regexes or `import()`
         * (Sentry S9/SA Basilisk 68, ST SeaMonkey 2.53, 30.10–01.11). Asked with a
         * RegExp built at runtime — no eval, so the CSP does not stand in the way.
         * An env without the field (older callers, tests) is not judged by it. */
        var below = !e.allSettled || !e.globalThis || !e.modules || e.propEscapes === false;
        if (!below && !e.at) {
            var ua = e.ua || '', m;
            if ((m = /(?:Chrome|Chromium|CriOS)\/(\d+)/.exec(ua)) && +m[1] >= 92) below = true;
            else if ((m = /Firefox\/(\d+)/.exec(ua)) && +m[1] >= 90) below = true;
            else if (!/Chrome|Chromium|CriOS|Firefox/.test(ua) && (m = /Version\/(\d+)\.(\d+)/.exec(ua)) && (+m[1] > 15 || (+m[1] === 15 && +m[2] >= 4))) below = true;
        }
        if (!env) engineVerdict = below;
        return below;
    }
    function isOldEngineParseError(vals, env) {
        var syntax = false;
        for (var i = 0; i < vals.length; i++) {
            var v = vals[i];
            if (v && v.type === 'SyntaxError' && !/JSON/i.test(String(v.value || ''))) { syntax = true; break; }
        }
        return syntax && engineBelowFloor(env);
    }

    function isForeignNoise(event) {
        try {
            var vals = event && event.exception && event.exception.values;
            if (!vals || !vals.length) return false; // message-only event — keep
            if (isLateParseError(vals)) return true; // parse error in the page after load — injected code
            if (isOldEngineParseError(vals)) return true; // an engine that cannot parse our language at all — "too old", not a defect
            if (isTruncatedOwnScript(vals)) return true; // our file cut short in transit — the ladder retries it
            if (isForeignBodyOwnScript(vals)) return true; // HTML under our file's address — the ladder retries it
            for (var i = 0; i < vals.length; i++) {
                if (privilegedThrow(vals[i])) continue; // Firefox's stand-in for an extension's exception — nothing of it is ours
                var st = vals[i] && vals[i].stacktrace;
                var frames = st && st.frames;
                if (!frames || !frames.length) continue;
                if (crossedExtension(frames)) continue; // this value ran through a browser extension — not ours to open, whatever else is in it
                if (docFramesForeign(frames)) continue; // named page-URL frames that none of our inline scripts declares — injected by the browser/host
                var foreignBlobs = minifiedBlobs(frames);
                for (var j = 0; j < frames.length; j++) {
                    if (frameIsOurs(frames[j], foreignBlobs)) return false; // a frame is ours — keep
                }
            }
            return true; // exception present, not one own frame anywhere — foreign noise
        } catch (_) {
            return false; // never let the filter itself drop a real event
        }
    }

    /**
     * Throw inside a wrapper that third-party code put over a NATIVE member.
     *
     * Some installs (ad SDK / ISP shim / extension / hooked WebView) replace
     * native DOM members with their own JS wrappers. A native member never
     * produces a JS stack frame, so a frame NAMED like one that lives in a file
     * we do not ship proves the injection, and the bug is in the injected code:
     * our own frames deeper down are only the innocent callers. Seen with
     * document.createElement (BG/BJ/BK), getBoundingClientRect (C1),
     * offsetHeight (C4/C5), a helper one call deeper (CF) — and, 29.10.2026,
     * a hook over WINDOW: `top.addEventListener` → `addEL_hook` reading
     * `null.tagName` (S0/S1/S2/RY, Android WebView 78, our only frame is the
     * plain `window.addEventListener('keydown', …)` in timbrica-core.js).
     * The receiver list therefore covers the global object's names as well.
     * Any frame counts, not only the throw site: the CF wrapper threw one
     * helper deeper than the member frame.
     */
    /* …and an ACCESSOR put over a native property: `get contentWindow` in an
     * anonymous script, called through `HTMLIFrameElement.get` → `Reflect.get`
     * (Sentry SE, 30.10.2026, Android 12 Chrome 131: a hook over the iframe's
     * contentWindow getter threw SecurityError reading the cross-origin window it
     * returns; our only frame was /privacy-audit's `frame.contentWindow` compare). A
     * native getter has no JS frame either, so a getter frame in a file we do not
     * ship is the same proof. */
    var PATCHED_MEMBER = /^(document\.createElement|[A-Za-z]*(?:HTML[A-Za-z]*Element|Element|Node|Document)\.(?:getBoundingClientRect|offsetHeight|offsetWidth|clientHeight|clientWidth|appendChild|removeChild|insertBefore|querySelector|querySelectorAll|addEventListener|removeEventListener|get|set)|(?:window|top|self|parent|globalThis|Window|EventTarget)\.(?:addEventListener|removeEventListener)|[gs]et [A-Za-z_$][\w$]*)$/;

    function threwInPatchedMember(event) {
        try {
            var vals = (event && event.exception && event.exception.values) || [];
            for (var i = 0; i < vals.length; i++) {
                var frames = (vals[i] && vals[i].stacktrace && vals[i].stacktrace.frames) || [];
                for (var j = frames.length - 1; j >= 0; j--) {
                    var f = frames[j] || {};
                    var file = f.filename || f.abs_path || '';
                    var foreign = !file || file === '<anonymous>' || file.indexOf('[native code]') !== -1;
                    if (foreign && PATCHED_MEMBER.test(f.function || '')) return true;
                }
            }
        } catch (_) { /* keep the event */ }
        return false;
    }

    /**
     * The part of a URL after # never belongs in an error report. /private-chat
     * keeps its end-to-end room key there (#k=…) — the one place that key is
     * meant to travel is the invite link itself — and the SDK copies
     * location.href into request.url and into navigation breadcrumbs, so any
     * error on that page would ship the key to the tracker (25.09.2026: found
     * while the tracker was still off on tembrica.com, where the chat lives —
     * switching it on without this would have made the leak real).
     * Mutates and returns the event; never throws.
     */
    function stripUrlFragments(event) {
        try {
            var cut = function (u) { return typeof u === 'string' ? u.replace(/#[\s\S]*$/, '') : u; };
            if (event && event.request && event.request.url) event.request.url = cut(event.request.url);
            var crumbs = event && event.breadcrumbs;
            var list = Array.isArray(crumbs) ? crumbs : ((crumbs && crumbs.values) || []);
            for (var i = 0; i < list.length; i++) {
                var d = list[i] && list[i].data;
                if (!d) continue;
                if (d.url) d.url = cut(d.url);
                if (d.from) d.from = cut(d.from);
                if (d.to) d.to = cut(d.to);
            }
        } catch (_) { /* a scrubber that throws would lose the whole report */ }
        return event;
    }

    window.SentryNoiseFilter = {
        stripUrlFragments: stripUrlFragments,
        isForeignNoise: isForeignNoise,
        threwInPatchedMember: threwInPatchedMember,
        isLateParseError: isLateParseError,
        isOldEngineParseError: isOldEngineParseError,
        crossedExtension: crossedExtension,
        BLOB_COLUMN_FLOOR: BLOB_COLUMN_FLOOR,
        FIRST_INLINE_SCRIPT_LINE: FIRST_INLINE_SCRIPT_LINE,
        DOC_COLUMN_CEIL: DOC_COLUMN_CEIL
    };
})();
